import { toIsoStringWithTimezone } from '@alfa-bank/site-helpers-transform/date/to-iso-string-with-timezone';
import { getCommaSeparation } from '@alfa-bank/site-helpers-transform/numbers/get-comma-separation';

import type { ICurrencies, TRatesType, IResponseRate, IRate, TRateTypeTag } from './models';

export type TRatesData = {
    [key in TRatesType]?: IRate[];
};

export type TRatesMaxDate = {
    [key in TRatesType]: string;
};

type TMaxDate = {
    [key in TRatesType]: number;
};

export const isNonEmptyArray = <T extends unknown[]>(arr?: T): arr is NonNullable<T> => {
    return Array.isArray(arr) && arr.length > 0;
};

export const prepareQueryParams = ({
    currencies,
    rateTypes,
}: {
    currencies: ICurrencies[];
    rateTypes: TRatesType[];
}) => {
    const actualDate = new Date();
    const currencyCode = currencies.reduce(
        (acc, { code }, idx) => acc + (idx === 0 ? code : `,${code}`),
        ''
    );

    return {
        'currencyCode.in': currencyCode,
        'rateType.in': rateTypes.join(','),
        'lastActualForDate.eq': true,
        'clientType.eq': 'standardCC',
        'date.lte': toIsoStringWithTimezone(actualDate),
    };
};

export const getDateString = (date: Date) =>
    date?.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Europe/Moscow',
    });

export const getTimeString = (date: Date) =>
    date?.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow',
    });

export const getFormatedDate = (date: Date) => {
    return `${getDateString(date)}, ${getTimeString(date)}, Мск`;
};

export const getFormatedCost = (n: number): string => `${getCommaSeparation(n)}`;

export const getRatesData = ({
    config,
    data,
    mobile,
    rateTypes,
}: {
    config: ICurrencies[];
    data: IResponseRate[];
    mobile?: boolean;
    rateTypes: TRatesType[];
}): {
    ratesData: TRatesData;
    ratesMaxDate: TRatesMaxDate;
} => {
    const maxDate = {} as TMaxDate;
    const getActualRates = (rateType: TRatesType) => {
        return config
            .sort((a, b) => (b.shortState ? 0 : -1))
            .map(({ code, iconUrl, name, shortState: short }) => {
                const currentCurrency = data.find(el => el.currencyCode === code);
                if (!currentCurrency) return null;
                const currentRate = currentCurrency?.rateByClientType[0].ratesByType.find(
                    x => x.rateType === rateType
                );

                if (!currentRate) return null;

                const {
                    buy: { originalValue: buy, valueWithCoefficient: buyWithCoef },
                    date,
                    sell: { originalValue: sell, valueWithCoefficient: sellWithCoef },
                } = currentRate.lastActualRate;

                maxDate[rateType] = maxDate[rateType]
                    ? Math.max(maxDate[rateType], +new Date(date))
                    : +new Date(date);

                return {
                    sell: getFormatedCost(sellWithCoef ?? sell),
                    buy: getFormatedCost(buyWithCoef ?? buy),
                    name: mobile ? name.slice(0, -6) : name,
                    short,
                    iconUrl,
                };
            })
            .filter(el => el !== null && el !== undefined);
    };

    const ratesData = rateTypes.reduce((acc: TRatesData, rateType) => {
        return { ...acc, [rateType]: getActualRates(rateType) };
    }, {} as TRatesData);

    const ratesMaxDate = rateTypes.reduce((acc: TRatesMaxDate, rateType) => {
        return { ...acc, [rateType]: getFormatedDate(new Date(maxDate[rateType])) };
    }, {} as TRatesMaxDate);

    return {
        ratesData,
        ratesMaxDate,
    };
};

export const preparedOptionsForFilter = (options: TRateTypeTag[]) =>
    options.map(option => ({ content: option.tagName, key: option.rateType }));
